import React, { useEffect, useRef, useState } from "react";
import { Select, Spin, Alert } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { fetchCasperID, fetchVessels, fetchVesselLocations } from "../../features/RawData/RawDataSlice";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
const { Option } = Select;

const currentYear = new Date().getFullYear();
const yearoptions = Array.from({ length: currentYear - 2023 + 1 }, (_, i) => {
  const year = 2023 + i;
  return { value: year.toString(), label: year.toString() };
});

const WorldMap = ({ vessels = [] }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const layersRef = useRef([]); // ← track ALL added layers (markers + polylines)

  useEffect(() => {
    if (mapInstanceRef.current) return;
    const map = L.map(mapRef.current, {
      center: [20, 10],
      zoom: 2,
      minZoom: 2,
      maxZoom: 8,
      zoomControl: true,
      scrollWheelZoom: false,
      attributionControl: false,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
    }).addTo(map);

    const styleEl = document.createElement("style");
    styleEl.id = "map-blue-style";
    styleEl.innerHTML = `
      .leaflet-container { background: #b8d9ea !important; border-radius: 10px; }
    `;
    if (!document.getElementById("map-blue-style")) {
      document.head.appendChild(styleEl);
    }

    mapInstanceRef.current = map;
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!L || !map) return;

    // ── Remove ALL previously added layers (markers AND polylines) ──
    layersRef.current.forEach((layer) => map.removeLayer(layer));
    layersRef.current = [];

    const addLayer = (layer) => {
      layer.addTo(map);
      layersRef.current.push(layer); // register so we can clear it next time
    };

    vessels.forEach((vessel) => {
      if (vessel.lat == null || vessel.lng == null || isNaN(vessel.lat) || isNaN(vessel.lng)) return;

      const popup = `<div style="font-family:sans-serif;font-size:12px;padding:6px;min-width:150px">
        ${vessel.isCurrent && vessel.isBoat ? '<div style="font-weight:700;color:#1d6fa8;margin-bottom:4px">📍 Current Position</div>' : ''}
        <strong>${vessel.name}</strong><br/>IMO: ${vessel.imo}<br/>
        Speed: ${vessel.speed ?? "N/A"} kts<br/>
        <span style="color:#64748b;font-size:11px">${vessel.date ? new Date(vessel.date).toLocaleDateString() : ""}</span>
      </div>`;

      if (vessel.isBoat) {
        const boatIcon = L.divIcon({
          className: "",
          html: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24"
            fill="#1d6fa8" stroke="#ffffff" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 20a2.4 2.4 0 0 0 2 1 2.4 2.4 0 0 0 2-1 2.4 2.4 0 0 1 2-1 2.4 2.4 0 0 1 2 1 2.4 2.4 0 0 0 2 1 2.4 2.4 0 0 0 2-1 2.4 2.4 0 0 1 2-1 2.4 2.4 0 0 1 2 1"/>
            <path d="M4 18l-1-5h18l-2 5"/><path d="M12 2v8"/><path d="M6.5 10h11l-1.5-4h-8z"/>
          </svg>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        });
        addLayer(L.marker([vessel.lat, vessel.lng], { icon: boatIcon }).bindPopup(popup));
      } else {
        const radius = vessel.isCurrent ? 6 : 4;
        const color  = vessel.isCurrent ? "#3b82f6" : "#60a5fa";
        const dot = L.circleMarker([vessel.lat, vessel.lng], {
          radius, fillColor: color, color: "#ffffff", weight: 1, opacity: 0.9, fillOpacity: 0.85,
        }).bindPopup(`<div style="font-family:sans-serif;font-size:12px;padding:4px;min-width:140px">
          <strong>${vessel.name}</strong><br/>IMO: ${vessel.imo}<br/>
          Speed: ${vessel.speed ?? "N/A"} kts<br/>
          <span style="color:#64748b;font-size:11px">${vessel.date ? new Date(vessel.date).toLocaleDateString() : ""}</span>
        </div>`);
        addLayer(dot);
      }
    });

const trackVesselsOnly = vessels.filter(v => !v.isBoat);

const trackPoints = trackVesselsOnly.map(v => [v.lat, v.lng]);

if (trackPoints.length > 1) {

  const polyline = L.polyline(trackPoints, {
    color: "#1677ff",
    weight: 3,
    opacity: 0.9,
    smoothFactor: 1,
    dashArray: "6, 6",
  });

  addLayer(polyline);

  const start = trackVesselsOnly[0];

  const startMarker = L.circleMarker([start.lat, start.lng], {
    radius: 6,
    fillColor: "#22c55e",
    color: "#ffffff",
    weight: 2,
    fillOpacity: 1,
  }).bindPopup(`<b>Start</b><br/>${start.name}`);

  addLayer(startMarker);

  const end = trackVesselsOnly[trackVesselsOnly.length - 1];

  const endMarker = L.circleMarker([end.lat, end.lng], {
    radius: 6,
    fillColor: "#ef4444",
    color: "#ffffff",
    weight: 2,
    fillOpacity: 1,
  }).bindPopup(`<b>End</b><br/>${end.name}`);

  addLayer(endMarker);
}
  }, [vessels]);

  return (
    <div
      ref={mapRef}
      style={{
        width: "100%",
        height: "100%",
        borderRadius: "10px",
        zIndex: 0,
        overflow: "hidden",
      }}
    />
  );
};

const ETSExposure = ({ data }) => {

   const { vesselCards } = useSelector((state) => state.rawData);
  
    // Redux takes priority; prop is fallback
    const cards_src = vesselCards || data || {};
  
   
    const summary = cards_src?.summary || {};
  
    const formatNumber = (num, decimals = 2) => {
    if (num == null || isNaN(num)) return "-";
    return Number(num).toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };
  const dispatch = useDispatch();

  const { vesselDetails, vesselStatus, casperID, globalFilters, vesselLocations, currentPosition, vesselSummary } =
    useSelector((state) => state.rawData);

  const [iMONumber, setIMONumber] = useState(globalFilters?.imoNumber || "");
  const [voyageYear, setVoyageYear] = useState(
    globalFilters?.year || yearoptions[yearoptions.length - 1].value
  );
  const [casperId, setCasperId] = useState(globalFilters?.casperID || "");

  useEffect(() => {
    setCasperId(globalFilters?.casperID || "");
  }, [globalFilters]);

  useEffect(() => {
    if (iMONumber) {
      dispatch(fetchCasperID({ imo_number: iMONumber, year: voyageYear }));
    }
  }, [iMONumber, voyageYear, dispatch]);

  const handleIMOChange    = (value) => { setIMONumber(value ?? ""); setCasperId(""); };
  const handleYearChange   = (value) => { setVoyageYear(value); setCasperId(""); };
  const handleCasperIdChange = (value) => { setCasperId(value ?? ""); };
  const [eurToUsd, setEurToUsd] = useState(null);

  const trackVessels = (Array.isArray(vesselLocations) ? vesselLocations : []).map(v => ({
    name: v.vessel_name || v.imo_number,
    imo:  v.imo_number,
    lat:  parseFloat(v.lat),
    lng:  parseFloat(v.lng),
    speed: v.speed,
    date: v.date_created,
    isCurrent: !currentPosition,
  }));

  const currentMarker = Array.isArray(currentPosition)
    ? currentPosition.map((v) => ({
        name: v.vessel_name || v.imo_number,
        imo: v.imo_number,
        lat: parseFloat(v.lat),
        lng: parseFloat(v.lng),
        speed: v.speed,
        date: v.date_created,
        isCurrent: true,
        isBoat: true,
      }))
    : currentPosition
    ? [{
        name: currentPosition.vessel_name || currentPosition.imo_number,
        imo: currentPosition.imo_number,
        lat: parseFloat(currentPosition.lat),
        lng: parseFloat(currentPosition.lng),
        speed: currentPosition.speed,
        date: currentPosition.date_created,
        isCurrent: true,
        isBoat: true,
      }]
    : [];

  const vessels = [...trackVessels, ...currentMarker];

  useEffect(() => {
  const fetchFX = async () => {
    try {
      const res = await fetch("https://open.er-api.com/v6/latest/USD");
      const data = await res.json();

      if (data.result === "success") {
        setEurToUsd(1 / data.rates.EUR);
      }
    } catch (err) {
      console.error("FX error:", err);
    }
  };

  fetchFX();
}, []);

  return (
    <div className="ets-exposure-section">
      <h3 className="section-title">ETS &amp; FEUM Exposure</h3>

      <div className="exposure-stats">
        <div className="stat-box">
          <div className="stat-label">Total Exposure</div>
         <div className="stat-value">
  {summary?.total_cost != null && eurToUsd
    ? `$${formatNumber(summary.total_cost * eurToUsd)}`
    : "-"}
</div>
        </div>
        <div className="stat-box">
          <div className="stat-label">EU Spot Price</div>
          
          <div className="stat-value" >€{(vesselSummary?.eu_rate_filter ?? data?.eu_eua_filter ?? 0).toLocaleString()}</div>
        </div>
        <div className="stat-box">
          <div className="stat-label">Fleet Emissions</div>
          <div className="stat-value" style={{ fontSize: "16px", fontWeight: "700", color: "#000000" }}>
             {vesselSummary?.fleet_emissions_co2 ?? data?.fleet_emission ?? "0.0"} T
          </div>
        </div>
        <div className="stat-box">
          <div className="stat-label">Fleet Count</div>
          <div className="stat-value">{vesselSummary?.fleet_count ?? data?.fleet_count ?? 0}</div>
        </div>
      </div>

      <div className="search-date-row" style={{ alignItems: "flex-end", gap: "16px", flexWrap: "wrap" }}>
      </div>

      <div className="map-section">
        <h4 className="map-title">ETS Exposure &amp; Fleet Emission Distribution</h4>
        <div className="world-map" style={{ borderRadius: "10px", overflow: "hidden" }}>
          <WorldMap vessels={vessels} />
        </div>
      </div>
    </div>
  );
};

export default ETSExposure;
